/**
 * 클라이언트측 회의록 텍스트 조립 — 호스트의 자막 복사·저장 버튼이 쓴다.
 *
 * 장문 문단(record.paragraph)이 하나라도 있으면 **본문 = 통합 문단,
 * 부록 = 문단별 원본 카드 + 통합되지 않은 꼬리**로 조립한다(서버
 * export.py 의 같은 결정 — 화면과 파일이 같은 구조). 문단이 없으면
 * 오늘 형식 그대로다(바이트 동일 — 플래그 OFF 방은 아무것도 변하지 않는다).
 */

import type { FeedItem, FinalItem, ParagraphItem } from './reducer'

function timeOf(ts: number): string {
  return new Date(ts).toLocaleTimeString('ko-KR')
}

/** 자막 한 건 → 오늘 형식 그대로의 줄(들린 내용 포함). */
function finalLine(i: FinalItem): string {
  const time = timeOf(i.ts)
  // 원문 가림과 무관하게 **항상 원문을 싣는다** — 가림은 화면의 배려일
  // 뿐이고 기록은 다 남아야 한다(lib/source-veil.ts). passthrough 는
  // 서버가 text 를 비워 보내므로(§6.5) 원문 줄이 애초에 없다.
  // text_raw(DEF-012 T3)도 가림과 무관하게 항상 싣는다.
  const lines = [`[${time}] ${i.data.ko}`]
  if (i.data.text) lines.push(`    (들린 내용) ${i.data.text}`)
  if (i.data.text_raw) {
    const suffix = i.data.text_raw_truncated ? ' (일부 생략)' : ''
    lines.push(`    (원전사${suffix}) ${i.data.text_raw}`)
  }
  return lines.join('\n')
}

export function buildTranscript(items: FeedItem[]): string {
  const finals = items.filter(
    (i): i is FinalItem => i.kind === 'final' && !i.deleted,
  )
  const paragraphs = items
    .filter((i): i is ParagraphItem => i.kind === 'paragraph')
    .sort((a, b) => a.data.ts - b.data.ts)

  // 시간순(오래된 것부터) — 리듀서 배열은 최신 우선이다.
  const chrono = finals.slice().reverse()

  if (paragraphs.length === 0) {
    return chrono.map(finalLine).join('\n')
  }

  const lines: string[] = []
  lines.push(`본문 — 통합 문단 (${paragraphs.length}건)`)
  lines.push('')
  for (const p of paragraphs) {
    const from = timeOf(p.data.ts)
    const to = timeOf(p.data.ts + Math.round(p.data.dur_s * 1000))
    const mark = p.data.uncertain ? ' [확인 필요]' : ''
    lines.push(`[${from}–${to}]${mark} ${p.data.ko}`)
    lines.push('')
  }

  lines.push('부록 — 원본 자막')
  lines.push('')
  const byUtt = new Map(finals.map((f) => [f.utt, f]))
  const covered = new Set<string>()
  paragraphs.forEach((p, idx) => {
    const members = p.data.utt_ids
      .map((u) => byUtt.get(u))
      .filter((f): f is FinalItem => f !== undefined)
    for (const m of members) covered.add(m.utt)
    lines.push(`문단 ${idx + 1} — 원본 ${members.length}건`)
    for (const m of members) lines.push(finalLine(m))
    lines.push('')
  })
  // 아직 어느 문단에도 통합되지 않은 실시간 꼬리 — 회의록이 반쪽이 되면
  // 안 된다(조용한 유실 금지와 같은 결).
  const tail = chrono.filter((f) => !covered.has(f.utt))
  if (tail.length > 0) {
    lines.push(`통합되지 않은 항목 (${tail.length}건)`)
    for (const f of tail) lines.push(finalLine(f))
    lines.push('')
  }
  // 마지막 빈 줄은 잘라 낸다 — 오늘 형식(끝에 개행 없음)과 결을 맞춘다.
  while (lines[lines.length - 1] === '') lines.pop()
  return lines.join('\n')
}
